import CognitoStatus from '@opsbase/lib-auth/CognitoStatus';
import StaffUserType from '@opsbase/lib-auth/StaffUserType';
import DataType from '@opsbase/lib-datatype/DataType/DataType';
import doNothing from '@opsbase/lib-general/utils/doNothing';
import i18n from '@opsbase/lib-i18n';
import { Pages } from '@opsbase/lib-pages';
import Palette from '@opsbase/lib-palette';
import App from '@opsbase/lib-system/App';
import { FeatureSets } from '@opsbase/lib-system/Features';
import useSelf from '@opsbase/react-ctrl-self/useSelf';
import useStaffFindById from '@opsbase/react-gql-hooks/staff/useStaffFindById';
import useStaffResendInvite from '@opsbase/react-gql-hooks/staff/useStaffResendInvite';
import useStaffTypeChange from '@opsbase/react-gql-hooks/staff/useStaffTypeChange';
import useStaffUnlock from '@opsbase/react-gql-hooks/staff/useStaffUnlock';
import useStaffUpdate from '@opsbase/react-gql-hooks/staff/useStaffUpdate';
import useUpload from '@opsbase/react-gql-hooks/uploadfile/useUpload';
import UserTypeChip from '@opsbase/web-chips/UserTypeChip';
import useCss from '@opsbase/web-css';
import useNewDataActions from '@opsbase/web-ctrl-newdata/useNewDataActions/useNewDataActions';
import PlaceChooser from '@opsbase/web-data-choosers/PlaceChooser';
import PositionChooser from '@opsbase/web-data-choosers/PositionChooser';
import Dialog from '@opsbase/web-dialogs/Dialog';
import DialogSize from '@opsbase/web-dialogs/Dialog/DialogSize';
import EmailIcon from '@opsbase/web-icons/icons/EmailIcon';
import MobileIcon from '@opsbase/web-icons/icons/MobileIcon';
import Horizontal from '@opsbase/web-layout/Horizontal';
import Pane from '@opsbase/web-layout/Pane';
import Vertical from '@opsbase/web-layout/Vertical';
import useNav from '@opsbase/web-nav/useNav/useNav';
import useParams from '@opsbase/web-nav/useParams';
import useNotificationActions from '@opsbase/web-notification/useNotificationActions';
import LanguagePicker from '@opsbase/web-pickers/LanguagePicker';
import TextField from '@opsbase/web-textfield/TextField';
import TextFieldWithStateAndBlur from '@opsbase/web-textfield/TextFieldWithStateAndBlur';
import Avatar from '@opsbase/web-ui/Avatar';
import Button from '@opsbase/web-ui/Button';
import Checkbox from '@opsbase/web-ui/Checkbox';
import FeatureFlagged from '@opsbase/web-ui/FeatureFlagged';
import Switch from '@opsbase/web-ui/Switch';
import Text from '@opsbase/web-ui/Text';
import React, { useCallback, useMemo, useState } from 'react';
const makeMyStyles = () => ({
    marginleft: {
        marginLeft: 10,
    },
});
export function StaffEditDialog() {
    const { id } = useParams();
    const updateStaff = useStaffUpdate();
    const upload = useUpload();
    const changeStaffType = useStaffTypeChange();
    const resendStaffInvite = useStaffResendInvite();
    const { raise } = useNotificationActions();
    const { loading, data: staff } = useStaffFindById({ variables: { id } });
    const self = useSelf();
    const [imageUploading, setImageUploading] = useState(false);
    const { updateItemsForType } = useNewDataActions();
    const unlockStaff = useStaffUnlock();
    const { goTo } = useNav();
    const isSuperOrPrimary = self.isSuperUser || self.isPrimary;
    const isPrimary = (staff === null || staff === void 0 ? void 0 : staff.staffType) === StaffUserType.PRIMARYADMIN;
    const isAdmin = (staff === null || staff === void 0 ? void 0 : staff.staffType) === StaffUserType.ADMIN;
    const isReg = (staff === null || staff === void 0 ? void 0 : staff.cognitoStatus) === CognitoStatus.REGISTERED;
    const isLocked = staff === null || staff === void 0 ? void 0 : staff.locked;
    const canChangeEmail = isSuperOrPrimary;
    let accessMsg;
    let hasGrant = false;
    let hasGrantPrimary = false;
    let hasRevokeGrant = false;
    let hasInvite = false;
    let hasInvitePrimary = false;
    let hasRevokeInvite = false;
    let hasResend = false;
    if (isPrimary) {
        accessMsg = i18n.t('access-primary');
        hasRevokeGrant = isReg && isSuperOrPrimary;
        hasRevokeInvite = !isReg && isSuperOrPrimary;
    }
    else if (isAdmin) {
        accessMsg = isReg ? i18n.t('access-manager') : i18n.t('access-invitedmanager');
        hasRevokeGrant = isReg;
        hasRevokeInvite = !isReg;
        hasResend = !isReg;
        hasGrantPrimary = isReg && isSuperOrPrimary;
    }
    else {
        accessMsg = i18n.t('access-staff');
        hasGrant = isReg;
        hasGrantPrimary = isReg && isSuperOrPrimary;
        hasInvite = !isReg;
        hasInvitePrimary = !isReg && isSuperOrPrimary;
    }
    const myCss = useCss(makeMyStyles);
    const doUpdateStaff = useMemo(() => async (patch) => {
        await updateStaff({
            id: staff.id,
            patch,
        }, {
            optimisticResponse: {
                __typename: 'Mutation',
                updateStaff: {
                    __typename: 'Staff',
                    ...staff,
                    ...patch,
                },
            },
        }).then(updatedStaff => {
            updateItemsForType(DataType.STAFF, [updatedStaff]);
        });
    }, [staff, updateItemsForType, updateStaff]);
    const handleChangeTitle = useCallback(title => {
        if (!title)
            return;
        doUpdateStaff({ title });
    }, [doUpdateStaff]);
    const handleRemoveImage = useCallback(async () => {
        await doUpdateStaff({ hasProfileImage: false });
    }, [doUpdateStaff]);
    const handleChangeImage = useCallback(async (profileImage) => {
        setImageUploading(true);
        await upload({
            fileName: staff.id,
            userImage: profileImage,
            isProfile: true,
        }, {
            context: { hasUpload: true },
        });
        await doUpdateStaff({
            hasProfileImage: true,
        });
        setImageUploading(false);
    }, [doUpdateStaff, staff, upload]);
    const handleClickChangeEmail = useCallback(() => goTo(Pages.STAFF_CHANGEEMAIL, { id }), [goTo, id]);
    const handleChangePlaces = useCallback(places => doUpdateStaff({
        places: places.map(({ id: theId }) => ({ id: theId })),
        allPlaces: false,
    }), [doUpdateStaff]);
    const handleChangeallPlaces = useCallback(() => doUpdateStaff({
        allPlaces: true,
        places: [],
    }), [doUpdateStaff]);
    const handleChangePositions = useCallback(positions => doUpdateStaff({
        positions: positions.map(({ id: theId }) => ({ id: theId })),
    }), [doUpdateStaff]);
    const handleChangeTel = useCallback(tel => doUpdateStaff({ tel }), [doUpdateStaff]);
    const handleChangeEnabled = useCallback(enabled => doUpdateStaff({ enabled }), [doUpdateStaff]);
    const handleGrant = useCallback(async () => {
        await raise(changeStaffType({
            id: staff.id,
            staffType: StaffUserType.ADMIN,
        }), i18n.t('admin-access-granted'));
    }, [changeStaffType, raise, staff]);
    const handleGrantPrimary = useCallback(async () => {
        await raise(changeStaffType({
            id: staff.id,
            staffType: StaffUserType.PRIMARYADMIN,
        }), i18n.t('admin-access-granted'));
    }, [changeStaffType, raise, staff]);
    const handleRevokeGrant = useCallback(async () => {
        await raise(changeStaffType({
            id: staff.id,
            staffType: StaffUserType.EMPLOYEE,
        }), i18n.t('admin-access-revoked'));
    }, [changeStaffType, raise, staff]);
    const handleInvite = useCallback(async () => {
        await raise(changeStaffType({
            id: staff.id,
            staffType: StaffUserType.ADMIN,
        }), i18n.t('invite-sent-successful'));
    }, [changeStaffType, raise, staff]);
    const handleInvitePrimary = useCallback(async () => {
        await raise(changeStaffType({
            id: staff.id,
            staffType: StaffUserType.PRIMARYADMIN,
        }), i18n.t('invite-sent-successful'));
    }, [changeStaffType, raise, staff]);
    const handleRevokeInvite = useCallback(async () => {
        await raise(changeStaffType({
            id: staff.id,
            staffType: StaffUserType.EMPLOYEE,
        }), i18n.t('revoke-invite-successful'));
    }, [changeStaffType, raise, staff]);
    const handleResendInvite = useCallback(() => raise(resendStaffInvite({ id: staff.id }), i18n.t('resend-invite-successful')), [raise, resendStaffInvite, staff]);
    const handleUnlock = useCallback(() => {
        unlockStaff({ id }).catch(doNothing);
    }, [id, unlockStaff]);
    const handleChangeLanguage = useCallback(language => {
        doUpdateStaff({
            preferences: { ...staff.preferences, language },
        }).catch(doNothing);
    }, [doUpdateStaff, staff]);
    return (React.createElement(Dialog, { size: DialogSize.X800_Y550, editableTitle: true, title: staff ? staff.title : undefined, onChangeTitle: handleChangeTitle, loading: loading, buttonsLeft: !!staff &&
            !isLocked && (React.createElement(Switch, { checked: staff.enabled, label: i18n.t('enabled'), onChange: handleChangeEnabled })), buttonsRight: React.createElement(FeatureFlagged, { flag: FeatureSets[App.OPSBASE].PEOPLE_MANAGEMENT_CHANGEUSERTYPE.key },
            React.createElement(Horizontal, { gutter: true },
                isLocked && isSuperOrPrimary && (React.createElement(Button, { color: Palette.BadRed, label: i18n.t('unlock-user'), onClick: handleUnlock })),
                hasGrant && !isLocked && (React.createElement(Button, { label: i18n.t('grant-admin-access'), onClick: handleGrant })),
                hasGrantPrimary && !isLocked && (React.createElement(Button, { label: i18n.t('grant-primaryadmin-access'), onClick: handleGrantPrimary })),
                hasInvite && !isLocked && (React.createElement(Button, { label: i18n.t('invite-as-place-admin'), onClick: handleInvite })),
                hasInvitePrimary && !isLocked && (React.createElement(Button, { label: i18n.t('invite-as-primaryadmin'), onClick: handleInvitePrimary })),
                hasRevokeGrant && !isLocked && (React.createElement(Button, { label: i18n.t('revoke-admin-access'), color: Palette.error, onClick: handleRevokeGrant })),
                hasRevokeInvite && !isLocked && (React.createElement(Button, { label: i18n.t('revoke-admin-invite'), color: Palette.error, onClick: handleRevokeInvite })),
                hasResend && !isLocked && (React.createElement(Button, { label: i18n.t('resend-invite'), onClick: handleResendInvite })))) }, staff && (React.createElement(Horizontal, null,
        React.createElement(Vertical, { centered: true, style: { maxWidth: '33%' }, padding: true, gutter: true },
            React.createElement(Pane, null,
                React.createElement(Avatar, { user: staff, allowEdit: true, uploading: imageUploading, onImageRemove: handleRemoveImage, onImageChange: handleChangeImage })),
            React.createElement(UserTypeChip, { userType: staff.staffType }),
            React.createElement(Text, null, accessMsg)),
        React.createElement(Vertical, { style: { maxWidth: '62%' }, gutter: true },
            React.createElement(Horizontal, { gutter: true, centered: true },
                React.createElement(EmailIcon, null),
                React.createElement(Pane, { grow: true },
                    React.createElement(TextField, { fullWidth: true, noBorder: true, disabled: true, className: myCss.marginleft, value: staff === null || staff === void 0 ? void 0 : staff.email })),
                !!canChangeEmail && (React.createElement(Pane, { shrink: true },
                    React.createElement(Button, { small: true, label: "Change", onClick: handleClickChangeEmail })))),
            React.createElement(Horizontal, { gutter: true, centered: true },
                React.createElement(MobileIcon, null),
                React.createElement(Pane, { grow: true },
                    React.createElement(TextFieldWithStateAndBlur
                    // noBorder
                    , { 
                        // noBorder
                        white: true, fullWidth: true, autoFocus: true, onBlur: handleChangeTel, placeholder: i18n.t('enter-telephone-number'), className: myCss.marginleft, value: staff === null || staff === void 0 ? void 0 : staff.tel }))),
            React.createElement(Pane, { paddingY: true },
                React.createElement(Text, null, i18n.t('staff-places')),
                React.createElement(PlaceChooser, { multi: true, value: staff.places, onChange: handleChangePlaces, allSelectable: true, allSelected: staff.allPlaces, onSelectAll: handleChangeallPlaces })),
            React.createElement(Pane, { paddingY: true },
                React.createElement(Text, null, i18n.t('staff-positions')),
                React.createElement(PositionChooser, { multi: true, value: staff.positions, onChange: handleChangePositions })),
            React.createElement(Pane, { paddingY: true },
                React.createElement(Text, null, i18n.t('prefered-language')),
                React.createElement(LanguagePicker, { white: true, noMargin: true, value: staff === null || staff === void 0 ? void 0 : staff.preferences.language, allowed: self.settings.languages, onChange: handleChangeLanguage })),
            React.createElement(Horizontal, { gutter: true, justify: "flex-start" },
                React.createElement(Checkbox, { disabled: true, checked: staff === null || staff === void 0 ? void 0 : staff.preferences.notifyByEmail, label: i18n.t('notify-staff-email') }),
                React.createElement(Checkbox, { disabled: true, checked: staff === null || staff === void 0 ? void 0 : staff.preferences.notifyByPush, label: i18n.t('notify-staff-push') })),
            staff.locked && (React.createElement(Pane, null,
                React.createElement(Text, { color: Palette.BadRed }, i18n.t('account-locked')))))))));
}
export default StaffEditDialog;
